import React from 'react';
import { motion } from 'framer-motion';
export interface MapScaleProps {
  /** The scale ratio, e.g. 10000 means 1:10.000 */
  scaleRatio: number;
  /** Length on the map in cm */
  mapLength?: number;
  /** Real-world length (calculated or provided) */
  realLength?: number;
  /** Unit for real-world length */
  unit?: 'cm' | 'm' | 'km';
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function formatNL(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(n);
}
function formatScale(ratio: number): string {
  return `1 : ${new Intl.NumberFormat('nl-NL').format(ratio)}`;
}
const UNIT_LABELS: Record<string, string> = {
  cm: 'centimeter',
  m: 'meter',
  km: 'kilometer'
};
export function MapScale({
  scaleRatio,
  mapLength,
  realLength,
  unit = 'km',
  label,
  mode = 'support'
}: MapScaleProps) {
  if (!scaleRatio || scaleRatio <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13124" data-mp="hbcen4akm9gt">
        <p className="text-red-600 text-sm font-medium" data-id="element-13125" data-mp="da0jkj8xgw18">Ongeldige schaal</p>
      </div>;
  }
  // Calculate real length if map length is provided
  const calculatedReal = mapLength !== undefined ? mapLength * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) : realLength;
  // Calculate map length if real length is provided
  const calculatedMap = realLength !== undefined && mapLength === undefined ? realLength * (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1) / scaleRatio : mapLength;
  // Scale bar segments (visual representation)
  const segments = 4;
  const segmentWidths = Array.from({
    length: segments
  }, (_, i) => i + 1);
  // Stimulus mode: compact scale bar
  if (mode === 'stimulus') {
    return <div className="bg-emerald-50 rounded-xl px-5 py-4 border border-emerald-200" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-13126" data-mp="37166gxcz47uz">
        {label && <p className="text-sm font-semibold text-emerald-800 mb-3 text-center" data-id="element-13127" data-mp="1fwvy8krqti5l">
            {label}
          </p>}
        {/* Scale ratio */}
        <div className="text-center mb-3" data-id="element-13128" data-mp="1kr07xbse84ad">
          <span className="inline-flex items-center bg-white rounded-lg px-3 py-1.5 border border-emerald-200 font-mono text-sm font-bold text-emerald-900" data-id="element-13129" data-mp="1op68g22n1nmu">
            {formatScale(scaleRatio)}
          </span>
        </div>
        {/* Scale bar */}
        <div className="flex justify-between text-[9px] text-slate-500 mb-0.5" style={{
        width: `${segments * 48}px`,
        margin: '0 auto'
      }} data-id="element-13130" data-mp="x0wefx8x5kx3">
          {Array.from({
          length: segments + 1
        }, (_, i) => <span key={i} className="w-4 text-center -ml-2" data-id="element-13131" data-mp="2hemxcpr1umeb">
                {i}
              </span>)}
        </div>
        <div className="flex items-end justify-center gap-0" data-id="element-13132" data-mp="2rhijfvq1golb">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: i * 0.1,
          duration: 0.3
        }} className={`h-3 w-12 border border-emerald-400 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-13133" data-mp="2mkaamlqs5wwq" data-mpi={i} />)}
        </div>
        <div className="flex justify-between text-[9px] text-emerald-700 font-bold mt-0.5" style={{
        width: `${segments * 48}px`,
        margin: '0 auto'
      }} data-id="element-13134" data-mp="1yufyy7i76ee2">
          {Array.from({
          length: segments + 1
        }, (_, i) => {
          const realVal = i * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1);
          return <span key={i} className="w-8 text-center -ml-4" data-id="element-13135" data-mp="52z2sdqn9kpf">
                  {formatNL(realVal)}
                </span>;
        })}
        </div>
        <div className="text-center mt-1 text-[9px] text-slate-400" data-id="element-13136" data-mp="3r9xkw07wlyjg">
          cm op kaart / {unit} in het echt
        </div>
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border-2 border-emerald-200 shadow-sm" aria-label={label ?? `Schaal ${formatScale(scaleRatio)}`} data-id="element-13137" data-mp="3iaak9gn00wrg">
      {label && <div className="mb-5 text-center" data-id="element-13138" data-mp="38kzf2538gc8u">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-emerald-200 shadow-sm" data-id="element-13139" data-mp="38hv1wi7si661">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-13140" data-mp="2zk16ralrhikg" />
            <span className="text-sm font-bold text-emerald-900" data-id="element-13141" data-mp="2fxpzvfyd4jd6">{label}</span>
          </div>
        </div>}

      {/* Scale ratio display */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4 text-center" data-id="element-13142" data-mp="2azy4gbyief48">
        <p className="text-xs text-emerald-600 font-medium mb-1" data-id="element-13143" data-mp="2oz7c2n8ng0wl">Schaal</p>
        <motion.p initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="text-2xl font-bold font-mono text-emerald-900" data-id="element-13144" data-mp="2kwawl5eeokwv">
          {formatScale(scaleRatio)}
        </motion.p>
        <p className="text-xs text-slate-500 mt-1" data-id="element-13145" data-mp="38ldd29xb07ps">
          1 cm op de kaart = {formatNL(scaleRatio)} cm in het echt
        </p>
      </div>

      {/* Scale bar visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-13146" data-mp="3hmjzezmmwcqy">
        {/* Top labels (Map length) */}
        <div className="flex justify-between text-[10px] text-slate-500 mb-1" style={{
        maxWidth: `${segments * 56}px`,
        margin: '0 auto'
      }} data-id="element-13147" data-mp="3cp1q384pfvif">
          {Array.from({
          length: segments + 1
        }, (_, i) => <span key={i} className="w-4 text-center -ml-2" data-id="element-13148" data-mp="2j9z951ch9d5a">
                {i}
              </span>)}
        </div>
        <div className="flex items-end justify-center gap-0 mb-1" data-id="element-13149" data-mp="2t1c0lrud61as">
          {segmentWidths.map((_, i) => <motion.div key={i} initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.2 + i * 0.1,
          duration: 0.3
        }} className={`h-4 w-14 border border-emerald-500 ${i % 2 === 0 ? 'bg-emerald-600' : 'bg-white'}`} style={{
          originX: 0
        }} data-id="element-13150" data-mp="2xzn9o0cu47ry" data-mpi={i} />)}
        </div>
        {/* Bottom labels (Real length) */}
        <div className="flex justify-between text-[10px] text-emerald-700 font-bold" style={{
        maxWidth: `${segments * 56}px`,
        margin: '0 auto'
      }} data-id="element-13151" data-mp="155dzcznhq6ve">
          {Array.from({
          length: segments + 1
        }, (_, i) => {
          const realVal = i * scaleRatio / (unit === 'km' ? 100000 : unit === 'm' ? 100 : 1);
          return <span key={i} className="w-8 text-center -ml-4" data-id="element-13152" data-mp="1j3vhmhummr5x">
                  {formatNL(realVal)}
                </span>;
        })}
        </div>
        <div className="text-center mt-2 text-xs text-slate-500" data-id="element-13153" data-mp="1a1cjgb1sd2q0">
          Boven: cm op kaart | Onder: {UNIT_LABELS[unit]} in het echt
        </div>
      </div>

      {/* Calculation example */}
      {(calculatedReal !== undefined || calculatedMap !== undefined) && <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 mb-4" data-id="element-13154" data-mp="1yoysg6u0ud4n">
          <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2" data-id="element-13155" data-mp="22r3jqywzyxo4">
            Berekening
          </h4>
          {calculatedMap !== undefined && calculatedReal !== undefined && <div className="space-y-1 text-sm text-slate-700" data-id="element-13156" data-mp="1tq5bu5jfu609">
              <p data-id="element-13157" data-mp="1tshs8e9js8b7">
                <strong data-id="element-13158" data-mp="2hfzoon3rbuki">{formatNL(calculatedMap)} cm</strong> op de kaart
              </p>
              <p className="text-emerald-600" data-id="element-13159" data-mp="2hic3ire7xotc">× {formatNL(scaleRatio)}</p>
              <div className="border-t border-emerald-200 pt-1" data-id="element-13160" data-mp="2mejeiylh1qi5">
                <p data-id="element-13161" data-mp="27qyan2grvn96">
                  ={' '}
                  <strong data-id="element-13162" data-mp="27o2bxbx3oujo">
                    {formatNL(calculatedReal)} {unit}
                  </strong>{' '}
                  in het echt
                </p>
              </div>
            </div>}
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-emerald-100 shadow-sm" data-id="element-13163" data-mp="356ltm5igsyqa">
        <div className="flex items-start gap-3" data-id="element-13164" data-mp="tps6ze3hefok">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13165" data-mp="toh37ea1d7xg">
            <span className="text-emerald-600 text-sm" data-id="element-13166" data-mp="12ivp3zp2ngpj">🗺️</span>
          </div>
          <div data-id="element-13167" data-mp="12kzqa9vl61m4">
            <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-13168" data-mp="yha5lvi1846d">
              Wat betekent de schaal?
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13169" data-mp="ar7gno9ezebz">
              Schaal <strong data-id="element-13170" data-mp="5smf9lifeoly">{formatScale(scaleRatio)}</strong> betekent: elke{' '}
              <strong data-id="element-13171" data-mp="5uh3p9swoyus">1 cm</strong> op de kaart is in werkelijkheid{' '}
              <strong data-id="element-13172" data-mp="jt6yujw60ogi">{formatNL(scaleRatio)} cm</strong>
              {scaleRatio >= 100 && <span data-id="element-13173" data-mp="es9kyji6ugkt">
                  {' '}
                  ({formatNL(scaleRatio / 100)} {UNIT_LABELS.m})
                </span>}
              {scaleRatio >= 100000 && <span data-id="element-13174" data-mp="1q7g0rj78rgyx">
                  {' '}
                  = {formatNL(scaleRatio / 100000)} {UNIT_LABELS.km}
                </span>}
              .
            </p>
          </div>
        </div>
      </div>
    </div>;
}